import { debounce } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../SearchInput';
import { SearchProps } from './Search.interface';
import { SearchSuggestions } from './SearchSuggestions';

export const Search = <RecordType extends object>({
  handleSearchInputChange,
  searchSuggestions,
  initialValue,
  appearance = 'filled-lighter',
}: SearchProps<RecordType>): JSX.Element => {
  const { t } = useTranslation();
  const [internalSearchTerm, setInternalSearchTerm] = useState<string>(initialValue || '');

  const handleSearchInputChangeDebounced = debounce(handleSearchInputChange, 750);

  const handleInputChange = useMemo(() => {
    return (term: string) => {
      handleSearchInputChangeDebounced.cancel();
      setInternalSearchTerm(term);
      if (term === '') {
        // A clear should be visible immediately
        handleSearchInputChange('');
      } else {
        handleSearchInputChangeDebounced(term);
      }
    };
  }, []);

  const handleSuggestionClick = (term: string) => {
    setInternalSearchTerm(term);
    handleSearchInputChange(term);
  };

  return (
    <div className="flex flex-col w-full searchWrapper">
      <SearchInput
        appearance={appearance}
        placeholder={t('search', { defaultValue: 'Suchen...' })}
        aria-label={t('search_input_label', { defaultValue: 'Suchen' })}
        onChange={(_, data) => {
          handleInputChange(data.value);
        }}
        onClear={() => handleInputChange('')}
        data-cy="search"
        value={internalSearchTerm}
      />
      {searchSuggestions.length > 0 && (
        <SearchSuggestions
          label={t('search_suggestions_label', {
            defaultValue: 'Suchvorschläge',
          })}
          suggestions={searchSuggestions}
          handleSuggestionClick={handleSuggestionClick}
        />
      )}
    </div>
  );
};
